
import Vue from "vue";
import defensiveCopy from "@/util/defensive-copy";
import { Component, Prop } from "vue-property-decorator";
import InlineRepeater from "@/components/Reusable/CustomInputs/InlineRepeater.vue";
import ColorInput from "@/components/Reusable/CustomInputs/Color/ColorInput.vue";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import { Shadow, ShadowValue, ShadowValueV, ShadowType } from "@/types/shadow";
import { pipe } from "fp-ts/lib/function";
import { fold } from "fp-ts/lib/Either";

@Component({
  components: {
    TextInput,
    ColorInput,
    InlineRepeater
  }
})
export default class ShadowInput extends Vue {
  @Prop()
  value!: ShadowValue;

  @Prop({
    default() {
      return {
        type: "box"
      };
    }
  })
  shadowOptions!: {
    type: ShadowType;
  };

  control = {
    options: {
      repeaterOptions: {
        buttonName: "Add Shadow"
      }
    }
  };

  get internalValue() {
    const defaultShadows: ShadowValue = {
      shadows: [],
      style: ""
    };

    return pipe(
      this.value,
      ShadowValueV.decode,
      fold(
        () => defaultShadows,
        shadows => shadows
      )
    );
  }

  get shadowDefault() {
    return {
      color: "#00000025",
      x: "5",
      y: "20",
      blur: "75",
      spread: "0",
      position: "outset"
    };
  }

  clear() {
    this.$emit("input", null);
  }

  update(shadows: Shadow[]) {
    if (!shadows.length) {
      this.clear();
      return;
    }

    const style = this.shadowsToCSS(shadows);
    this.$emit("input", { shadows, style });
  }

  updateField(
    newValue: string | null,
    repeaterIndex: number,
    property: keyof Shadow
  ) {
    const newShadows = defensiveCopy(this.value.shadows) || [];
    newShadows[repeaterIndex][property] = newValue;

    this.update(newShadows);
  }

  shadowToCSS(shadow: Shadow) {
    const boxShadow = [shadow.x, shadow.y, shadow.blur, shadow.spread];
    const textShadow = [shadow.x, shadow.y, shadow.blur];

    const unit = "px"; // TODO: Support any unit?
    const values = this.shadowOptions.type == "box" ? boxShadow : textShadow;

    const valuesWithUnit = values
      .map(value => value || 0) // Turn undefined values into zeroes to preserve their position
      .map(value => `${value}${unit}`) // Add css unit
      .join(" ");

    return `${valuesWithUnit} ${shadow.color}`;
  }

  shadowsToCSS(shadows: Shadow[]) {
    return shadows.map(this.shadowToCSS).join(", ");
  }
}
